/**
 * Chunk Manager - Performance Optimization for Magic Sandbox
 * 
 * Divides the simulation grid into chunks and tracks which chunks are active.
 * Inactive (sleeping) chunks are skipped during simulation updates.
 */

import { Chunk, ChunkManager, DirtyRect } from './types';

// Chunk size - 16x16 cells per chunk
export const CHUNK_SIZE = 16;

// Frames before a chunk goes to sleep
export const SLEEP_THRESHOLD = 10;

/**
 * Creates a new chunk manager for the given grid dimensions
 */
export function createChunkManager(
  gridWidth: number = 180,
  gridHeight: number = 120
): ChunkManager {
  const chunksX = Math.ceil(gridWidth / CHUNK_SIZE);
  const chunksY = Math.ceil(gridHeight / CHUNK_SIZE);
  
  const chunks: Chunk[][] = [];
  for (let cy = 0; cy < chunksY; cy++) {
    chunks[cy] = [];
    for (let cx = 0; cx < chunksX; cx++) {
      chunks[cy][cx] = {
        x: cx,
        y: cy,
        isActive: true,
        sleepCounter: 0,
        lastActivityFrame: 0,
        modifiedCells: new Set(),
      };
    }
  }

  return {
    chunks,
    chunkWidth: CHUNK_SIZE,
    chunkHeight: CHUNK_SIZE,
    chunksX,
    chunksY,
    activeChunkCount: chunksX * chunksY,
    sleepingChunkCount: 0,
    dirtyRects: [],
    frameCount: 0,
  };
}

/**
 * Gets the chunk coordinates for a given cell position
 */
export function getChunkCoords(
  chunkManager: ChunkManager,
  cellX: number,
  cellY: number
): { cx: number; cy: number } {
  return {
    cx: Math.floor(cellX / chunkManager.chunkWidth),
    cy: Math.floor(cellY / chunkManager.chunkHeight),
  };
}

/**
 * Marks a cell as modified, waking its chunk if needed
 */
export function markCellModified(
  chunkManager: ChunkManager,
  cellX: number,
  cellY: number
): void {
  const { cx, cy } = getChunkCoords(chunkManager, cellX, cellY);
  
  if (cx >= 0 && cx < chunkManager.chunksX && cy >= 0 && cy < chunkManager.chunksY) {
    const chunk = chunkManager.chunks[cy][cx];
    chunk.isActive = true;
    chunk.sleepCounter = 0;
    chunk.lastActivityFrame = chunkManager.frameCount;
    chunk.modifiedCells.add(`${cellX},${cellY}`);
  }
}

/**
 * Checks if a chunk should be processed (is active)
 */
export function shouldProcessChunk(
  chunkManager: ChunkManager,
  cx: number,
  cy: number
): boolean {
  if (cx < 0 || cx >= chunkManager.chunksX || cy < 0 || cy >= chunkManager.chunksY) {
    return false;
  }
  return chunkManager.chunks[cy][cx].isActive;
}

/**
 * Gets the bounds of a chunk in cell coordinates
 */
export function getChunkBounds(
  chunkManager: ChunkManager,
  cx: number,
  cy: number
): { x: number; y: number; width: number; height: number } {
  const x = cx * chunkManager.chunkWidth;
  const y = cy * chunkManager.chunkHeight;
  const width = Math.min(chunkManager.chunkWidth, 180 - x);
  const height = Math.min(chunkManager.chunkHeight, 120 - y);
  return { x, y, width, height };
}

/**
 * Updates the sleep state of all chunks
 * Chunks that haven't had activity for SLEEP_THRESHOLD frames go to sleep
 */
export function updateChunkSleepState(chunkManager: ChunkManager): void {
  let activeCount = 0;
  let sleepingCount = 0;

  for (let cy = 0; cy < chunkManager.chunksY; cy++) {
    for (let cx = 0; cx < chunkManager.chunksX; cx++) {
      const chunk = chunkManager.chunks[cy][cx];

      if (chunk.isActive) {
        // Check if chunk should go to sleep
        const framesSinceActivity = chunkManager.frameCount - chunk.lastActivityFrame;
        if (framesSinceActivity >= SLEEP_THRESHOLD && chunk.modifiedCells.size === 0) {
          chunk.sleepCounter++;
          if (chunk.sleepCounter >= SLEEP_THRESHOLD) {
            chunk.isActive = false;
            sleepingCount++;
          } else {
            activeCount++;
          }
        } else {
          chunk.sleepCounter = 0;
          activeCount++;
        }
      } else {
        sleepingCount++;
      }

      // Clear modified cells for next frame
      chunk.modifiedCells.clear();
    }
  }

  chunkManager.activeChunkCount = activeCount;
  chunkManager.sleepingChunkCount = sleepingCount;
}

/**
 * Wakes all chunks (useful when loading a scene or applying global effects)
 */
export function wakeAllChunks(chunkManager: ChunkManager): void {
  for (let cy = 0; cy < chunkManager.chunksY; cy++) {
    for (let cx = 0; cx < chunkManager.chunksX; cx++) {
      const chunk = chunkManager.chunks[cy][cx];
      chunk.isActive = true;
      chunk.sleepCounter = 0;
      chunk.lastActivityFrame = chunkManager.frameCount;
    }
  }
  chunkManager.activeChunkCount = chunkManager.chunksX * chunkManager.chunksY;
  chunkManager.sleepingChunkCount = 0;
}

/**
 * Wakes a specific chunk and its neighbors (useful for localized effects)
 */
export function wakeChunkAndNeighbors(
  chunkManager: ChunkManager,
  cx: number,
  cy: number
): void {
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (nx >= 0 && nx < chunkManager.chunksX && ny >= 0 && ny < chunkManager.chunksY) {
        const chunk = chunkManager.chunks[ny][nx];
        chunk.isActive = true;
        chunk.sleepCounter = 0;
        chunk.lastActivityFrame = chunkManager.frameCount;
      }
    }
  }
}

/**
 * Adds a dirty rectangle for optimized rendering
 */
export function addDirtyRect(
  chunkManager: ChunkManager,
  rect: DirtyRect
): void {
  chunkManager.dirtyRects.push(rect);
}

/**
 * Clears all dirty rectangles
 */
export function clearDirtyRects(chunkManager: ChunkManager): void {
  chunkManager.dirtyRects = [];
}

/**
 * Gets performance metrics from the chunk manager
 */
export function getChunkMetrics(chunkManager: ChunkManager): {
  activeChunks: number;
  sleepingChunks: number;
  totalChunks: number;
  activePercentage: number;
} {
  const total = chunkManager.chunksX * chunkManager.chunksY;
  return {
    activeChunks: chunkManager.activeChunkCount,
    sleepingChunks: chunkManager.sleepingChunkCount,
    totalChunks: total,
    activePercentage: (chunkManager.activeChunkCount / total) * 100,
  };
}
